import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_LOCALE, directionOf, isLocale, type Locale } from '@awj/i18n';

import { messages, type MessageKey } from './messages.ts';

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, values?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const STORED = 'awj.admin-locale';

/**
 * The admin's language, chosen by the member of staff using it.
 *
 * Unlike the storefront the language is not in the URL — a back office is not indexed, not
 * shared as links, and staff pick a language once and keep it. Remembered per browser instead,
 * which is what makes it stick across shifts on a shared terminal.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      const stored = localStorage.getItem(STORED);

      return isLocale(stored) ? stored : DEFAULT_LOCALE;
    } catch {
      return DEFAULT_LOCALE;
    }
  });

  // The document, not a wrapper: `dir` on <html> is what the token layer and the component kit
  // key off, and scoping it to a container would leave the toasts and dialogs in the top layer
  // pointing the wrong way.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = directionOf(locale);
    /*
     * The stylesheets read the locale as well as the direction.
     *
     * Arabic sets visually larger than Latin at the same pixel value, so the reference's type
     * scale is pulled in for Arabic and opened back out under `:root[data-locale="en"]` — along
     * with the leading and the optical tracking that Latin display sizes want and joined Arabic
     * letterforms must never have. Without this attribute the English build reads at the Arabic
     * scale and its headings stop leading.
     */
    document.documentElement.dataset.locale = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);

    try {
      localStorage.setItem(STORED, next);
    } catch {
      // Storage disabled; the choice lasts for this visit and no longer.
    }
  }, []);

  const t = useCallback(
    (key: MessageKey, values?: Record<string, string | number>) => {
      const template: string = messages[locale][key] ?? key;

      return values
        ? template.replace(/\{(\w+)\}/g, (match, name: string) =>
            name in values ? String(values[name]) : match,
          )
        : template;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used inside an <I18nProvider>.');
  }

  return context;
}

/** The common case: just the translator. */
export function useT() {
  return useI18n().t;
}
